import A11yDialog from 'a11y-dialog'

import { Tenant } from 'types/shopConfig'
import { TENANT_COM } from 'shared/consts'
import { CUSTOMER_REDIRECTION_MODAL_ID } from 'views/consts'

import Storage from 'views/assets/scripts/services/storage/storage'
import { trackEvent } from 'shared/experiments/utils'

import { EVENT_TRIGGERED, REDIRECTION_TENANTS } from './consts'

import GeoFetcher from './GeoFetcher'

/**
 * A helper class that handles showing the Customer Redirection Modal.
 * The modal is shown when the AT/CH customer visits the COM shop
 * (it is impossible to make a checkout there for non-COM customers).
 */
export default class CustomerRedirectionModal {
  static STORAGE_KEY_SHOWN = 'CustomerRedirectionModal__shown'

  private readonly storage: Storage
  private readonly geoFetcher: GeoFetcher
  private readonly modalContainer: HTMLElement | null
  private readonly dialog: A11yDialog
  private tenant: Tenant = TENANT_COM

  static getTenantSpecificSelector(tenant: Tenant) {
    return `[data-clientside-hook="customerRedirection__tenantSpecific"][data-tenant="${tenant}"]`
  }

  constructor() {
    this.storage = new Storage('sessionStorage')
    this.geoFetcher = new GeoFetcher(process.env.NODE_ENV)
    this.modalContainer = document.querySelector<HTMLElement>(
      `#${CUSTOMER_REDIRECTION_MODAL_ID}[data-clientside-hook="modal"]`
    )
    if (this.modalContainer) {
      this.dialog = new A11yDialog(this.modalContainer)
    }
  }

  shouldTrigger(customerCountryCode: Tenant): boolean {
    const isShown = this.storage.get(
      `${CustomerRedirectionModal.STORAGE_KEY_SHOWN}-${customerCountryCode}`
    )

    return !isShown && REDIRECTION_TENANTS.includes(customerCountryCode)
  }

  chargeTenantSpecificTexts() {
    const elements = document.querySelectorAll(
      CustomerRedirectionModal.getTenantSpecificSelector(this.tenant)
    )

    elements.forEach(el => el.removeAttribute('aria-hidden'))
  }

  handleCloseClick = () => {
    this.storage.set(
      `${CustomerRedirectionModal.STORAGE_KEY_SHOWN}-${this.tenant}`,
      true
    )
  }

  bindClosingEvents() {
    const closingTriggers = document.querySelectorAll(
      `[data-clientside-hook~="CustomerRedirectionModalCloser"]`
    )

    closingTriggers.forEach(el =>
      el.addEventListener('click', this.handleCloseClick)
    )
  }

  bindBackdropClick() {
    this.modalContainer?.addEventListener('click', event => {
      if ((<HTMLElement>event.target).tagName.toLowerCase() === 'dialog') {
        this.dialog.hide()
      }
    })
  }

  bindEvents() {
    this.bindClosingEvents()
    this.bindBackdropClick()
  }

  showForTenant(tenant: Tenant) {
    this.tenant = tenant

    this.chargeTenantSpecificTexts()
    this.dialog.show()
    this.bindEvents()
  }

  async init() {
    if (!this.modalContainer) {
      return
    }

    // Geo data is requested
    const customerCountryCode: Tenant = (await this.geoFetcher.fetch()).country
    if (!this.shouldTrigger(customerCountryCode)) {
      return
    }

    this.tenant = customerCountryCode

    this.chargeTenantSpecificTexts()
    this.dialog.show()
    this.bindEvents()

    trackEvent(EVENT_TRIGGERED)
  }
}
